import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../api/client';
import {
  listNotifications,
  markRead,
  type Notification,
  type NotificationKind,
} from '../api/notifications';
import { AppShell } from '../components/layout/AppShell';
import { useNotifications } from '../notifications/useNotifications';
import { Badge, Button, EmptyState, ErrorState, LoadingState } from '../components/ui';
import type { BadgeTone } from '../components/ui';
import './NotificationsPage.css';

type State = 'loading' | 'ready' | 'error';

const TONES: Record<NotificationKind, BadgeTone> = {
  NEW_FOLLOWER: 'brand',
  LIKE: 'accent',
  COMMENT: 'accent',
  MODERATION_APPROVED: 'success',
  MODERATION_REJECTED: 'warning',
};

const LABELS: Record<NotificationKind, string> = {
  NEW_FOLLOWER: 'Follower',
  LIKE: 'Like',
  COMMENT: 'Comment',
  MODERATION_APPROVED: 'Approved',
  MODERATION_REJECTED: 'Not approved',
};

function when(value: string): string {
  return new Date(value).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * `/notifications` — what happened while you were away.
 *
 * Reading the page marks nothing: what is unread is unread until the person
 * says otherwise, because a list that clears itself on sight is a list you
 * cannot come back to.
 */
export function NotificationsPage() {
  const { refresh } = useNotifications();
  const [state, setState] = useState<State>('loading');
  const [items, setItems] = useState<Notification[]>([]);
  const [unread, setUnread] = useState(0);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const load = useCallback(async () => {
    try {
      const inbox = await listNotifications();
      setItems(inbox.results);
      setUnread(inbox.unread);
      setState('ready');
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
      setState('error');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const readAll = async () => {
    setWorking(true);
    try {
      await markRead();
      await load();
      // The badge in the shell is showing a number this just changed.
      await refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
    } finally {
      setWorking(false);
    }
  };

  const header = (
    <div className="sc-page-header">
      <div>
        <h1>Notifications</h1>
        <p>{unread > 0 ? `${unread} unread` : 'Nothing unread.'}</p>
      </div>
      {unread > 0 && (
        <div className="sc-page-header__actions">
          <Button size="sm" variant="secondary" isLoading={working} onClick={() => void readAll()}>
            Mark all read
          </Button>
        </div>
      )}
    </div>
  );

  if (state === 'loading') {
    return (
      <AppShell header={header}>
        <LoadingState label="Looking…" />
      </AppShell>
    );
  }

  if (state === 'error') {
    return (
      <AppShell header={header}>
        <ErrorState
          title="We could not load your notifications"
          description={error?.message}
          requestId={error?.requestId}
          onRetry={() => void load()}
        />
      </AppShell>
    );
  }

  return (
    <AppShell header={header}>
      {items.length === 0 ? (
        <EmptyState
          title="Nothing yet"
          description="When someone follows you, likes or comments on a story, or a moderator decides about one, it appears here."
        />
      ) : (
        <ul className="sc-notifications">
          {items.map((item) => (
            <li
              key={item.id}
              className={`sc-notifications__item${item.is_read ? '' : ' is-unread'}`}
            >
              <Badge tone={TONES[item.kind]} srPrefix="Kind">
                {LABELS[item.kind]}
              </Badge>
              <span className="sc-notifications__text">
                <span className="sc-notifications__title">
                  {item.link ? <Link to={item.link}>{item.title}</Link> : item.title}
                </span>
                {item.body && <span className="sc-notifications__body">{item.body}</span>}
              </span>
              <span className="sc-notifications__when">
                {!item.is_read && <span className="sc-visually-hidden">Unread. </span>}
                {when(item.created_at)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </AppShell>
  );
}
